import { z } from "zod";

export const listingTypeSchema = z.enum(["RENT", "SALE", "RENT_AND_SALE"]);
export const rentalPricingTypeSchema = z.enum(["FIXED_PERIOD", "PER_DAY"]);
export const depositTypeSchema = z.enum(["NONE", "FIXED_AMOUNT", "PERCENTAGE"]);
export const depositBaseSchema = z.enum(["RENTAL_PRICE", "REPLACEMENT_VALUE"]);

/**
 * docs/business-rules.md §8.4:
 * - `FIXED_AMOUNT` requires a non-negative `value`.
 * - `PERCENTAGE` requires `value` in [0, 100] and a `base`.
 * - `NONE` ignores `value`/`base`.
 */
export const productDepositSchema = z
  .object({
    type: depositTypeSchema,
    value: z.number().min(0).optional(),
    base: depositBaseSchema.optional(),
    note: z.string().trim().max(2000).optional(),
  })
  .superRefine((deposit, ctx) => {
    if (deposit.type === "FIXED_AMOUNT" && deposit.value === undefined) {
      ctx.addIssue({
        code: z.ZodIssueCode.custom,
        message: "value is required when deposit type is FIXED_AMOUNT.",
        path: ["value"],
      });
    }

    if (deposit.type === "PERCENTAGE") {
      if (deposit.value === undefined || deposit.value < 0 || deposit.value > 100) {
        ctx.addIssue({
          code: z.ZodIssueCode.custom,
          message: "value must be between 0 and 100 when deposit type is PERCENTAGE.",
          path: ["value"],
        });
      }
      if (!deposit.base) {
        ctx.addIssue({
          code: z.ZodIssueCode.custom,
          message: "base is required when deposit type is PERCENTAGE.",
          path: ["base"],
        });
      }
    }
  });
export type ProductDepositInput = z.infer<typeof productDepositSchema>;

/**
 * docs/business-rules.md §6.3: rental products (`RENT` / `RENT_AND_SALE`)
 * must have a rental price. The backend re-validates this at publish time
 * regardless (this schema only improves the seller form UX).
 */
export const createProductSchema = z
  .object({
    categoryId: z.string().uuid().optional(),
    name: z.string().trim().min(1).max(250),
    description: z.string().trim().max(5000).optional(),
    listingType: listingTypeSchema,
    gender: z.string().trim().max(30).optional(),
    condition: z.string().trim().max(30).optional(),
    rentalPricingType: rentalPricingTypeSchema.optional(),
    rentalPrice: z.number().min(0).optional(),
    salePrice: z.number().min(0).optional(),
    replacementValue: z.number().min(0).optional(),
    currency: z.string().trim().length(3).optional(),
    deposit: productDepositSchema.optional(),
  })
  .superRefine((product, ctx) => {
    if (
      (product.listingType === "RENT" || product.listingType === "RENT_AND_SALE") &&
      product.rentalPrice === undefined
    ) {
      ctx.addIssue({
        code: z.ZodIssueCode.custom,
        message: "rentalPrice is required for RENT / RENT_AND_SALE listings.",
        path: ["rentalPrice"],
      });
    }
  });
export type CreateProductInput = z.infer<typeof createProductSchema>;

/** No explicit null-clearing in the MVP (mirrors `updateStoreSchema`) — an omitted field leaves the current value unchanged. */
export const updateProductSchema = z.object({
  categoryId: z.string().uuid().optional(),
  name: z.string().trim().min(1).max(250).optional(),
  description: z.string().trim().max(5000).optional(),
  listingType: listingTypeSchema.optional(),
  gender: z.string().trim().max(30).optional(),
  condition: z.string().trim().max(30).optional(),
  rentalPricingType: rentalPricingTypeSchema.optional(),
  rentalPrice: z.number().min(0).optional(),
  salePrice: z.number().min(0).optional(),
  replacementValue: z.number().min(0).optional(),
  currency: z.string().trim().length(3).optional(),
  deposit: productDepositSchema.optional(),
});
export type UpdateProductInput = z.infer<typeof updateProductSchema>;

export const productSearchQuerySchema = z.object({
  q: z.string().trim().max(200).optional(),
  categoryId: z.string().uuid().optional(),
  storeId: z.string().uuid().optional(),
  listingType: listingTypeSchema.optional(),
  gender: z.string().trim().max(30).optional(),
  size: z.string().trim().max(100).optional(),
  color: z.string().trim().max(100).optional(),
  condition: z.string().trim().max(30).optional(),
  minRentalPrice: z.coerce.number().min(0).optional(),
  maxRentalPrice: z.coerce.number().min(0).optional(),
  province: z.string().trim().max(100).optional(),
  district: z.string().trim().max(100).optional(),
  page: z.coerce.number().int().min(1).default(1),
  limit: z.coerce.number().int().min(1).max(100).default(20),
  sort: z.enum(["publishedAt", "rentalPrice", "relevance"]).optional(),
});
export type ProductSearchQueryInput = z.infer<typeof productSearchQuerySchema>;
